import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Eye, RotateCw, X, Ban, ChevronLeft, ChevronRight, Search, RefreshCw, Wallet, CreditCard, CheckCheck, Trash2 } from "lucide-react";
import JobPreviewDialog from "@/pages/store/JobPreviewDialog";

const statusColor = {
  queued: "bg-blue-500", printing: "bg-amber-500 pulse-ring", printed: "bg-emerald-500",
  failed: "bg-red-500", cancelled: "bg-slate-400", payment_pending: "bg-slate-300",
  pending_approval: "bg-amber-500", rejected: "bg-red-400",
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

export default function StoreJobs() {
  const [tab, setTab] = useState("open");
  const [payMethod, setPayMethod] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: DEFAULT_PAGE_SIZE, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => { setSelectedIds(new Set()); }, [tab, page, payMethod, dateFrom, dateTo, search]);

  const toggleSel = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelAll = (jobsWithFiles) => {
    setSelectedIds((prev) => {
      const eligibleIds = jobsWithFiles.map((j) => j.id);
      const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(eligibleIds);
    });
  };
  const bulkDeleteFiles = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete uploaded files from ${ids.length} selected job${ids.length === 1 ? "" : "s"}?\n\nJob records are kept for accounting; only the documents are removed from Print Plus storage.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/store/jobs/bulk-delete-files", { job_ids: ids });
      toast.success(`Removed ${data.files_deleted} file${data.files_deleted === 1 ? "" : "s"} across ${data.jobs_processed} job${data.jobs_processed === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
    finally { setBulkBusy(false); }
  };

  // If we're deep-linked here (e.g. from a "Review" toast), open the preview dialog.
  useEffect(() => {
    const j = searchParams.get("job");
    if (j) {
      setPreviewId(j);
      // clean the URL so re-navigating doesn't reopen it accidentally
      const next = new URLSearchParams(searchParams);
      next.delete("job");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status_group: tab, page, page_size: pageSize, paginated: true };
      if (payMethod !== "all") params.payment_method = payMethod;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.q = search.trim();
      const { data: res } = await api.get("/store/jobs", { params });
      setData(res);
    } catch (e) { toast.error("Could not load jobs"); }
    finally { setLoading(false); }
  }, [tab, payMethod, dateFrom, dateTo, search, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);
  useEffect(() => { setPage(1); }, [tab, payMethod, dateFrom, dateTo, search, pageSize]);

  const retry = async (id) => { await api.post(`/store/jobs/${id}/retry`); toast.success("Retrying"); load(); };
  const reprint = async (j) => {
    if (!window.confirm(`Send job ${j.job_number} to the printer again? The customer will NOT be charged.`)) return;
    try {
      const { data: r } = await api.post(`/store/jobs/${j.id}/retry`);
      toast.success(`Reprinting ${j.job_number}${r.reprint_count > 1 ? ` (#${r.reprint_count})` : ""}`);
      load();
    } catch (e) { toast.error("Reprint failed"); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this job?")) return;
    await api.post(`/store/jobs/${id}/cancel`); toast.success("Cancelled"); load();
  };
  const reject = async (j) => {
    const reason = window.prompt(`Reject cash order ${j.job_number}?\n\nOptional reason (shown in audit log):`, "");
    if (reason === null) return;
    try {
      await api.post(`/store/jobs/${j.id}/reject`, { reason });
      toast.success(`Rejected ${j.job_number}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };
  const complete = async (j) => {
    if (!window.confirm(`Mark job ${j.job_number} as printed / completed?\n\nUse this when you've handed the print-out to the customer or the job is finished outside the agent.`)) return;
    try {
      await api.post(`/store/jobs/${j.id}/complete`);
      toast.success(`${j.job_number} marked complete`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark complete"); }
  };

  const resetFilters = () => { setPayMethod("all"); setDateFrom(""); setDateTo(""); setSearch(""); };
  const jobs = data.items || [];

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-4" data-testid="store-jobs-page">
      <div className="flex items-start justify-between gap-3">
        <div><h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Print Jobs</h1><p className="text-slate-500 text-sm">Live queue · reprint · reject cash · reconcile.</p></div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-jobs"><RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="jobs-tabs">
          <TabsTrigger value="open" data-testid="tab-open">Open jobs</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filter bar */}
      <Card className="border-slate-200">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[160px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Job # or file name" className="h-9 pl-8 text-sm" data-testid="filter-search" />
          </div>
          <Select value={payMethod} onValueChange={setPayMethod}>
            <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="filter-payment"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All payments</SelectItem>
              <SelectItem value="online">Online paid</SelectItem>
              <SelectItem value="cash">Cash / COD</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[145px] text-sm" data-testid="filter-date-from" />
          <span className="text-slate-400 text-xs">→</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[145px] text-sm" data-testid="filter-date-to" />
          {(payMethod !== "all" || dateFrom || dateTo || search) && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9 text-slate-500" data-testid="filter-reset"><X className="w-3 h-3 mr-1" />Clear</Button>
          )}
          <div className="ml-auto text-xs text-slate-500 font-mono">{data.total} result{data.total === 1 ? "" : "s"}</div>
        </CardContent>
      </Card>

      {/* Bulk actions bar — only for Completed tab */}
      {tab === "completed" && selectedIds.size > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm" data-testid="bulk-bar">
          <span className="font-mono text-orange-800">{selectedIds.size} selected</span>
          <Button size="sm" onClick={bulkDeleteFiles} disabled={bulkBusy} className="ml-auto h-8 bg-red-500 hover:bg-red-600 text-white text-xs" data-testid="bulk-delete-files">
            <Trash2 className="w-3 h-3 mr-1" />{bulkBusy ? "Deleting…" : "Delete files"}
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear">Clear</Button>
        </div>
      )}

      {/* Compact table */}
      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="[&_th]:h-9 [&_th]:py-1.5 [&_th]:px-2 [&_th]:text-[10px] [&_th]:uppercase [&_th]:tracking-wider [&_td]:py-1.5 [&_td]:px-2 [&_td]:text-xs">
            <TableHeader>
              <TableRow className="border-b">
                {tab === "completed" && (
                  <TableHead className="w-8 pl-3">
                    <Checkbox
                      checked={jobs.filter((j) => (j.files || []).length > 0 && !j.files_deleted).length > 0 && jobs.filter((j) => (j.files || []).length > 0 && !j.files_deleted).every((j) => selectedIds.has(j.id))}
                      onCheckedChange={() => toggleSelAll(jobs.filter((j) => (j.files || []).length > 0 && !j.files_deleted))}
                      data-testid="bulk-select-all"
                    />
                  </TableHead>
                )}
                <TableHead>Job #</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Files</TableHead>
                <TableHead className="text-right">Pages</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Pay</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.length === 0 && !loading && (
                <TableRow><TableCell colSpan={tab === "completed" ? 9 : 8} className="text-center py-10 text-slate-400 text-sm">No {tab === "open" ? "open" : "completed"} jobs match your filters.</TableCell></TableRow>
              )}
              {jobs.map((j) => {
                const isCash = j.payment_method === "cash" || j.payment_status === "cod";
                const hasFiles = (j.files || []).length > 0 && !j.files_deleted;
                const filesGone = !!j.files_deleted;
                return (
                  <TableRow
                    key={j.id}
                    className={`${filesGone ? "opacity-70" : "cursor-pointer hover:bg-slate-50"}`}
                    onClick={filesGone ? undefined : () => setPreviewId(j.id)}
                    data-testid={`job-row-${j.job_number}`}
                  >
                    {tab === "completed" && (
                      <TableCell className="pl-3" onClick={(e) => e.stopPropagation()}>
                        {hasFiles ? (
                          <Checkbox checked={selectedIds.has(j.id)} onCheckedChange={() => toggleSel(j.id)} data-testid={`bulk-select-${j.job_number}`} />
                        ) : (
                          <span className="inline-block w-4 h-4" title={j.files_deleted ? "Files already deleted" : "No files"} />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="font-mono font-semibold text-slate-800 whitespace-nowrap">
                      {j.job_number}
                      {j.reprint_count > 0 && <div className="text-[9px] text-orange-500 font-normal">reprint ×{j.reprint_count}</div>}
                      {j.files_deleted && <div className="text-[9px] text-slate-400 font-normal">files purged</div>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-slate-500 font-mono text-[11px]">{new Date(j.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</TableCell>
                    <TableCell className="max-w-[220px] truncate" title={(j.files || []).map((f) => f.filename).join(", ")}>{(j.files || []).map((f) => f.filename).join(", ")}</TableCell>
                    <TableCell className="text-right font-mono">{j.total_pages}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{fmtINR(j.amount)}</TableCell>
                    <TableCell>
                      {isCash
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"><Wallet className="w-2.5 h-2.5" />cash</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"><CreditCard className="w-2.5 h-2.5" />online</span>}
                    </TableCell>
                    <TableCell><Badge className={`${statusColor[j.status] || "bg-slate-500"} text-[10px] font-semibold py-0.5 px-1.5`}>{j.status}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap space-x-0.5" onClick={(e) => e.stopPropagation()}>
                      {filesGone ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-1" data-testid={`files-purged-${j.job_number}`} title="Files were permanently deleted for this job">
                          <Trash2 className="w-2.5 h-2.5" />files purged
                        </span>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => setPreviewId(j.id)} data-testid={`preview-${j.job_number}`}><Eye className="w-3 h-3 mr-1" />View</Button>
                          {j.status === "pending_approval" && (
                            <>
                              <Button size="sm" className="h-7 px-2 text-[11px] bg-emerald-500 hover:bg-emerald-600" onClick={() => setPreviewId(j.id)} data-testid={`approve-${j.job_number}`}>Approve</Button>
                              {isCash && <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => reject(j)} data-testid={`reject-${j.job_number}`}><Ban className="w-3 h-3 mr-1" />Reject</Button>}
                            </>
                          )}
                          {j.status === "failed" && <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => retry(j.id)} data-testid={`retry-${j.job_number}`}>Retry</Button>}
                          {j.status === "printed" && (j.payment_status === "paid" || j.payment_status === "cod") && <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => reprint(j)} data-testid={`reprint-${j.job_number}`}><RotateCw className="w-3 h-3 mr-1" />Reprint</Button>}
                          {j.status === "queued" && <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => cancel(j.id)} data-testid={`cancel-${j.job_number}`}>Cancel</Button>}
                          {["queued", "printing", "failed"].includes(j.status) && (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => complete(j)} data-testid={`complete-${j.job_number}`}>
                              <CheckCheck className="w-3 h-3 mr-1" />Mark done
                            </Button>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data.pages > 1 && (
        <div className="flex items-center justify-between text-xs text-slate-500 flex-wrap gap-2" data-testid="pagination">
          <div>Page {data.page} of {data.pages} · {data.total} total</div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400">Rows:</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="h-8 w-[70px] text-xs" data-testid="page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{PAGE_SIZE_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8 px-2" disabled={data.page <= 1} onClick={() => setPage(Math.max(1, data.page - 1))} data-testid="page-prev"><ChevronLeft className="w-3 h-3" /></Button>
            <Button size="sm" variant="outline" className="h-8 px-2" disabled={data.page >= data.pages} onClick={() => setPage(Math.min(data.pages, data.page + 1))} data-testid="page-next"><ChevronRight className="w-3 h-3" /></Button>
          </div>
        </div>
      )}
      {data.pages <= 1 && data.total > 5 && (
        <div className="flex items-center justify-end gap-2 text-xs text-slate-500">
          <span className="text-slate-400">Rows:</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="h-8 w-[70px] text-xs" data-testid="page-size"><SelectValue /></SelectTrigger>
            <SelectContent>{PAGE_SIZE_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <JobPreviewDialog
        jobId={previewId}
        open={!!previewId}
        onOpenChange={(v) => { if (!v) setPreviewId(null); }}
        onChanged={load}
      />
    </div>
  );
}
